import React, { useState, useEffect } from 'react';
import { SealCheck, Info, ArrowClockwise, Trash, Tray, X, Check } from '@phosphor-icons/react';
import {
  getBranchNames as fetchBranchNames, getGroupCode as fetchGroupCode, getTickets as fetchTickets,
  getFinalSettlement as fetchFinalSettlement, getFinalSettlementDetails as fetchFinalSettlementDetails,
  saveData, deleteFinalMemoDetails,
} from '../api/connectionRegistryApi';
import { useLoginName } from '../utils/auditUser';
import SearchableSelect from './SearchableSelect';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

// A ticket can carry several sureties at once, stored as one comma-separated
// string — "GUARANTOR", "GUARANTOR, SECURITY LIEN", "CHIT PLEDGE, GUARANTOR".
// Mirrors Getfinalsetlementdetails in ChangeDAL.cs, which splits on commas and
// looks for a GUARANTOR token before it fetches the first memo rows: whenever
// the backend sends first memo data, this screen must show the grid for it.
const hasGuarantorSurety = (suretyNameValue) => String(suretyNameValue || '')
  .split(',')
  .some((part) => part.trim().toLowerCase() === 'guarantor');

const MEMO_COLUMNS = [
  { key: 'file_name', label: 'File Name', mono: true },
  { key: 'approved_file_name', label: 'Approved File Name', mono: true },
  { key: 'authorized_date', label: 'Authorized Date', date: true },
  { key: 'approved_date', label: 'Approved Date', date: true },
  { key: 'approved_by', label: 'Approved By' },
  { key: 'authorized_by', label: 'Authorized By' },
];

// ── Memo records table, standing in for PrimeNG p-table w/ paginator ──
//
// Each grid is a self-contained titled panel. The two memo grids sit directly
// above one another, so without the panel chrome and column rules they read as
// a single continuous table. The header row stays rendered when there are no
// rows — the empty message occupies a full-width cell inside the table, the way
// the Angular emptymessage template does, rather than replacing the whole grid.
//
// Each instance keeps its own `page` state; the two grids never share it since
// they are distinct elements with distinct `key`s.
function MemoTable({ title, rows, emptyText, testId }) {
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [rows]);
  const pageSize = 10;
  const paginated = rows.length > pageSize;
  const totalPages = Math.ceil(rows.length / pageSize) || 1;
  const visibleRows = paginated ? rows.slice(page * pageSize, page * pageSize + pageSize) : rows;

  const thStyle = {
    textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #E5E7EB',
    borderRight: '1px solid #E5E7EB', color: '#6B7280', fontWeight: '600',
    fontSize: '11px', textTransform: 'uppercase', whiteSpace: 'nowrap', background: '#FFFFFF',
  };
  const tdStyle = {
    padding: '8px 10px', borderBottom: '1px solid #F3F4F6', borderRight: '1px solid #F3F4F6',
    fontSize: '13px', color: '#111827', whiteSpace: 'nowrap',
  };

  const cellValue = (row, col) => {
    if (col.date) return formatDate(row[col.key]);
    if (col.mono) return row[col.key] || '—';
    return row[col.key] ?? '—';
  };

  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden', background: 'white' }} data-testid={testId}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '10px 12px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
        <div style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{title}</div>
        <span style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280', background: '#F3F4F6', border: '1px solid #E5E7EB', padding: '2px 8px', borderRadius: '999px' }}>
          {rows.length} {rows.length === 1 ? 'record' : 'records'}
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '640px', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {MEMO_COLUMNS.map((col, i) => (
                <th key={col.key} style={i === MEMO_COLUMNS.length - 1 ? { ...thStyle, borderRight: 'none' } : thStyle}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={MEMO_COLUMNS.length} style={{ padding: 0, borderBottom: 'none' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: '24px', color: '#9CA3AF' }}>
                    <Tray size={22} />
                    <div style={{ fontSize: '12px' }}>{emptyText}</div>
                  </div>
                </td>
              </tr>
            ) : visibleRows.map((row, idx) => (
              <tr key={row.file_name || idx}>
                {MEMO_COLUMNS.map((col, i) => (
                  <td
                    key={col.key}
                    style={{
                      ...tdStyle,
                      ...(col.mono ? { fontFamily: 'monospace' } : null),
                      ...(i === MEMO_COLUMNS.length - 1 ? { borderRight: 'none' } : null),
                    }}
                  >
                    {cellValue(row, col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {paginated && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', padding: '8px 12px', borderTop: '1px solid #E5E7EB', fontSize: '12px', color: '#6B7280' }}>
          <button type="button" className="btn-secondary" style={{ padding: '4px 10px', fontSize: '12px' }} disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
            Prev
          </button>
          <span>Page {page + 1} of {totalPages}</span>
          <button type="button" className="btn-secondary" style={{ padding: '4px 10px', fontSize: '12px' }} disabled={page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}>
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function FinalSettlementCompleted() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);
  const { toast, setToast, showToast } = useToast();

  const [branchlist, setBranchlist] = useState([]);
  const [branchCodeList, setBranchCodeList] = useState([]); // groups
  const [tickets, setTickets] = useState([]);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');
  const [ticket, setTicket] = useState('');
  const [reason, setReason] = useState('');

  const [suretyname, setSuretyname] = useState('');
  const [firstMemoGuarantorRecords, setFirstMemoGuarantorRecords] = useState([]);
  const [finalMemoGuarantorRecords, setFinalMemoGuarantorRecords] = useState([]);

  const [branchschema, setBranchschema] = useState(null);
  const [branchCode, setBranchCode] = useState(null); // mirrors Angular's this.branchCode
  const [groupCode, setGroupCode] = useState(null);
  const [chitgroupId, setChitgroupId] = useState(null);

  const [touched, setTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingReason, setPendingReason] = useState('');

  useEffect(() => {
    fetchBranchNames().then((res) => setBranchlist(res || [])).catch(() => setBranchlist([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const resetFromTicket = () => {
    setSuretyname('');
    setFirstMemoGuarantorRecords([]);
    setFinalMemoGuarantorRecords([]);
    setReason('');
  };

  const resetFromGroup = () => {
    setGroupCode(null);
    setChitgroupId(null);
    setTicket('');
    resetFromTicket();
    setTickets([]);
  };

  const resetFromBranch = () => {
    setBranchschema(null);
    setBranchCode(null);
    setGroup('');
    resetFromGroup();
    setBranchCodeList([]);
    setTickets([]);
  };

  const loadGroups = async (branch) => {
    const schema = branch.branch_code;
    setBranchschema(schema);
    setBranchCode(schema); // Angular: this.branchCode = event.branch_code
    try {
      const res = await fetchGroupCode(schema);
      setBranchCodeList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setBranchCodeList([]);
    }
  };

  const loadTickets = async (schema, gCode) => {
    try {
      const res = await fetchTickets(schema, gCode);
      setTickets(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setTickets([]);
    }
  };

  // STEP 4: Getfinalsetlementdetails → bind grids
  const loadFinalSettlementDetails = async (suretyNameValue, gCode, ticketNo) => {
    try {
      const res = await fetchFinalSettlementDetails(suretyNameValue, gCode, ticketNo);
      const row = Array.isArray(res) && res.length > 0 ? res[0] : null;
      if (!row) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);

      // Final Memo: always bind, whatever suretyname is.
      setFinalMemoGuarantorRecords(row?.finalMemo ?? []);

      // First Memo: relevant whenever Guarantor is one of the sureties, alone
      // or alongside others.
      setFirstMemoGuarantorRecords(
        hasGuarantorSurety(suretyNameValue) ? (row?.firstMemo ?? []) : []
      );
    } catch (err) {
      console.error('Failed to fetch final settlement details:', err);
      setFirstMemoGuarantorRecords([]);
      setFinalMemoGuarantorRecords([]);
    }
  };

  // STEP 1: ticket selected → call Getfinalsetlement
  const handleTicketSelected = async (selectedTicket, gCode) => {
    try {
      const res = await fetchFinalSettlement(branchschema, gCode, selectedTicket.ticketno);
      const row = Array.isArray(res) && res.length > 0 ? res[0] : null;

      // STEP 2: bind surety_name to the read-only textbox
      const surety = row?.surety_name ?? '';
      setSuretyname(surety);

      // STEP 3: Final Memo always loads regardless of suretyname;
      // First Memo is filtered inside loadFinalSettlementDetails.
      await loadFinalSettlementDetails(surety, gCode, selectedTicket.ticketno);
    } catch (err) {
      console.error('Failed to fetch final settlement info:', err);
      setSuretyname('');
      setFirstMemoGuarantorRecords([]);
      setFinalMemoGuarantorRecords([]);
      showToast('error', 'Load Failed', 'Failed to fetch final settlement info.');
    }
  };

  const handleCaoChange = (value) => {
    setCao(value || '');
    resetFromBranch();
    if (!value) return;
    const selected = branchlist.find((x) => x.branch_name === value);
    if (selected) loadGroups(selected);
  };

  const handleGroupChange = (value) => {
    setGroup(value || '');
    resetFromGroup();
    if (!value) return;
    const selected = branchCodeList.find((x) => x.group_Code === value);
    if (selected) {
      setGroupCode(selected.group_Code);
      setChitgroupId(selected.chitgroup_id);
      loadTickets(branchschema, selected.group_Code);
    }
  };

  const handleTicketChange = (value) => {
    setTicket(value || '');
    resetFromTicket();
    if (!value) return;
    const selected = tickets.find((x) => x.ticketno === value);
    if (selected) handleTicketSelected(selected, groupCode);
  };

  const handleReasonKeyDown = (e) => {
    const max = 200;
    if (
      reason.length >= max &&
      e.key !== 'Backspace' && e.key !== 'Delete' &&
      e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' &&
      e.key !== 'Tab'
    ) {
      e.preventDefault();
    }
  };

  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const ticketInvalid = touched && !ticket;
  const reasonInvalid = touched && (reason.length < 10 || reason.length > 200);

  /** Step 1: validate + open confirm modal (replaces Angular's window.confirm). */
  const confirmDelete = () => {
    setTouched(true);

    if (reason.length < 10 || reason.length > 200) {
      showToast('warn', 'Incomplete Form', 'Please enter a valid reason (min 10 characters).');
      return;
    }

    if (!ticket) {
      showToast('warn', 'No Selection', 'Please select a ticket first.');
      return;
    }

    setPendingReason(reason);
    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (isSaving) return;
    setShowConfirmModal(false);
  };

  /** Step 2: fires only after "Yes, Delete" — saves the audit record, then deletes. */
  const doDelete = async () => {
    setIsSaving(true);

    const ticketNo = Number(ticket);

    const audit = {
      schemaName: branchschema,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'FINAL_SETTLEMENT_MEMO_DELETE',
      oldData: {
        branchCode,
        groupCode,
        chitGroupId: chitgroupId,
        ticketNo,
        suretyName: suretyname,
        firstMemoRecords: firstMemoGuarantorRecords,
        finalMemoRecords: finalMemoGuarantorRecords,
      },
      newData: { status: 'DELETED' },
      reason,
    };

    try {
      await saveData([audit]);

      try {
        await deleteFinalMemoDetails(branchschema, chitgroupId, ticketNo, groupCode);
        handleDeleteSuccess();
      } catch (err) {
        setIsSaving(false);
        setShowConfirmModal(false);
        console.error('Delete failed:', err);
        showToast('warn', 'Partial Success', 'Audit saved, but delete failed. Please try again.', true);
      }
    } catch (err) {
      setIsSaving(false);
      setShowConfirmModal(false);
      console.error('Audit save failed:', err);
      showToast('error', 'Save Failed', 'Audit save failed. Delete was not performed.', true);
    }
  };

  const handleDeleteSuccess = () => {
    setIsSaving(false);
    setShowConfirmModal(false);
    showToast('success', 'Success', 'Final memo details deleted successfully.', true);
    handleClear();
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setTouched(false);
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="fsc-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="final-settlement-form">
        {isSaving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SealCheck size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Chit Management</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Final Settlement Completed - Guarantor Deletion</div>
          </div>
        </div>

        {/* SECTION A: Select Ticket */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group code and ticket to load settlement records</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleCaoChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="fsc-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>CAO is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={branchCodeList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a branch first"
              disabled={!cao}
              error={groupInvalid}
              testId="fsc-group-select"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={tickets}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket"
              emptyMessage="Select a group first"
              disabled={!group}
              error={ticketInvalid}
              testId="fsc-ticket-select"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        {/* SECTION B: Settlement Records */}
        {ticket && (
          <>
            <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Settlement Records</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Surety details and memo records for the selected ticket</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Surety Name</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={suretyname || ''} readOnly data-testid="fsc-surety-name" />
              </div>
            </div>

            {/* The two grids are separate panels so they never read as one table. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '16px' }}>
              {/* First Memo Guarantor Records: whenever Guarantor is one of the sureties. */}
              {hasGuarantorSurety(suretyname) && (
                <MemoTable
                  key="first-memo-grid"
                  title="First Memo Guarantor Records"
                  rows={firstMemoGuarantorRecords}
                  emptyText="No first memo guarantor records found."
                  testId="fsc-first-memo-table"
                />
              )}

              {/* Final Memo Guarantor Records: ALWAYS shown once a ticket is selected. */}
              <MemoTable
                key="final-memo-grid"
                title="Final Memo Guarantor Records"
                rows={finalMemoGuarantorRecords}
                emptyText="No final memo guarantor records found."
                testId="fsc-final-memo-table"
              />
            </div>
          </>
        )}

        {/* SECTION C: Reason */}
        {ticket && (
          <>
            <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Provide a reason for this deletion (min 10 characters)</div>
              </div>
            </div>

            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Reason <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <textarea
              className="input-field"
              style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
              rows={3}
              maxLength={200}
              placeholder="Enter reason (min 10 characters)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={handleReasonKeyDown}
              data-testid="fsc-reason"
            />
            {reasonInvalid && (
              <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
                {reason.length === 0 ? 'Reason is required.' : reason.length < 10 ? 'Minimum 10 characters required.' : 'Maximum 200 characters allowed.'}
              </div>
            )}
          </>
        )}

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={isSaving} data-testid="fsc-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              className="btn-primary"
              style={{ background: isSaving || !ticket ? '#F3A6A6' : '#DC2626', borderColor: '#DC2626' }}
              disabled={isSaving || !ticket}
              data-testid="fsc-delete-button"
            >
              <Trash size={14} style={{ marginRight: '4px' }} /> {isSaving ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal (replaces window.confirm) */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="fsc-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Trash size={18} color="#DC2626" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Final Memo Deletion</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to delete the final memo details for ticket <strong>{ticket}</strong>. This action cannot be undone.
              </p>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={isSaving} className="btn-secondary" data-testid="fsc-confirm-cancel">
                Cancel
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={isSaving}
                data-testid="fsc-confirm-delete"
                style={{
                  display: 'inline-flex', alignItems: 'center', padding: '8px 16px', fontSize: '13px', fontWeight: '600',
                  color: 'white', background: isSaving ? '#F3A6A6' : '#DC2626', border: 'none', borderRadius: '6px',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                }}
              >
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FinalSettlementCompleted;